import {
  ClipboardCopyIcon,
  UserGroupIcon,
  UsersIcon,
  DesktopComputerIcon as DesktopComputerIconOutline,
  TicketIcon,
  QuestionMarkCircleIcon,
  MailIcon,
  ViewGridIcon as ViewGridIconOutline,
  NewspaperIcon,
  QrcodeIcon,
  ReceiptTaxIcon,
  SwitchHorizontalIcon,
  ViewGridAddIcon,
  MusicNoteIcon,
  UserIcon as UserIconOutline,
  CogIcon,
  ShoppingBagIcon,
} from "@heroicons/react/outline";
import {
  ViewBoardsIcon,
  UserGroupIcon as SolidUserGroupIcon,
  FlagIcon,
  DesktopComputerIcon,
  CubeIcon,
  PhotographIcon,
  UserIcon,
  ViewGridIcon,
  TemplateIcon,
  UsersIcon as UsersIconSolid,
  PencilIcon,
  ReceiptTaxIcon as ReceiptTaxIconSolid,
} from "@heroicons/react/solid";
import type { IResourceItem } from "@refinedev/core";
import {
  Activity,
  CSIcon,
  EVoucherIcon,
  ImageIcon,
  LockIcon,
  Runner,
  ShoeIcon,
  Ticket2Icon,
} from "components/customIcons";
import { ScanIcon } from "components/customIcons/scan";

export const resources: IResourceItem[] = [
  {
    name: "Dashboard",
    list: "/dashboard/overview",
    clone: "/dashboard/event",
    meta: {
      icon: <ViewBoardsIcon className="nav-icon" aria-hidden="true" />,
      app: "dashboard",
      resource: "",
    },
  },
  {
    name: "Event",
    meta: {
      icon: <TicketIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Sports",
    list: "/events/sports",
    create: "/events/sports/create",
    show: "/events/sports/:id",
    edit: "/events/sports/:id/edit",
    meta: {
      icon: <Runner className="nav-icon" aria-hidden="true" />,
      parent: "Event",
      label: "Sport events management",
      app: "events",
      resource: "sports",
    },
  },
  {
    name: "Attractions",
    list: "/events/attractions",
    create: "/events/attractions/create",
    edit: "/events/attractions/:id/edit",
    meta: {
      icon: <Activity className="nav-icon" aria-hidden="true" />,
      parent: "Event",
      label: "Attraction events management",
      app: "events",
      resource: "attractions",
    },
  },
  {
    name: "Attractions QR code",
    list: "/events/attraction-qr-code",
    show: "/events/attraction-qr-code/:id",
    create: "/events/attraction-qr-code/create",
    meta: {
      icon: <QrcodeIcon className="nav-icon" aria-hidden="true" />,
      parent: "Event",
      label: "Attractions QR Code",
      app: "events",
      resource: "attractions_qr_code",
    },
  },
  {
    name: "Private code/email",
    list: "/events/passcodes",
    create: "/events/passcodes/create",
    meta: {
      icon: <LockIcon className="nav-icon" aria-hidden="true" />,
      parent: "Event",
      app: "events",
      resource: "private_code_email",
    },
  },
  {
    name: "Addons",
    list: "/events/addons",
    create: "/events/addons/create",
    show: "/events/addons/:id",
    edit: "/events/addons/:id/edit",
    meta: {
      icon: <ShoppingBagIcon className="nav-icon" aria-hidden="true" />,
      parent: "Event",
      label: "Event Addons",
      app: "events",
      resource: "addons",
    },
  },
  {
    name: "Orders",
    list: "/orders",
    show: "/orders/:id",
    edit: "/orders/:id/edit",
    meta: {
      icon: <TemplateIcon className="nav-icon" aria-hidden="true" />,
      app: "orders",
      resource: "",
    },
  },
  {
    name: "Groups",
    list: "/group-management",
    show: "/group-management/detail/:id",
    edit: "/group-management/edit/:id",
    meta: {
      icon: <SolidUserGroupIcon className="nav-icon" aria-hidden="true" />,
      label: "Group Management",
      app: "group_buy_management",
      resource: "",
    },
  },
  {
    name: "Participants",
    meta: {
      icon: <ViewGridAddIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Sport",
    list: "/participants/sport",
    show: "/participants/sport/:id",
    edit: "/participants/sport/:id/edit",
    create: "/participants/sport/mass-import",
    meta: {
      icon: <Runner className="nav-icon" aria-hidden="true" />,
      parent: "Participants",
      label: "Sport participants",
      app: "participants",
      resource: "sports",
    },
  },
  {
    name: "Attraction",
    list: "/participants/attraction",
    show: "/participants/attraction/:id",
    edit: "/participants/attraction/:id/edit",
    create: "/participants/attraction/mass-import",
    meta: {
      icon: <TicketIcon className="nav-icon" aria-hidden="true" />,
      parent: "Participants",
      label: "Attraction participants",
      app: "participants",
      resource: "attractions",
    },
  },
  {
    name: "Promotion",
    meta: {
      icon: <ReceiptTaxIconSolid className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Coupon",
    list: "/promotion/coupon",
    show: "/promotion/coupon/:id",
    edit: "/promotion/coupon/:id/edit",
    create: "/promotion/coupon/create/:type",
    meta: {
      icon: <EVoucherIcon className="nav-icon" aria-hidden="true" />,
      parent: "Promotion",
      label: "Coupon",
      app: "promotion",
      resource: "coupon",
    },
  },
  {
    name: "Condition",
    list: "/promotion/condition",
    show: "/promotion/condition/:id",
    edit: "/promotion/condition/:id/edit",
    meta: {
      icon: <CogIcon className="nav-icon" aria-hidden="true" />,
      parent: "Promotion",
      label: "Condition",
      app: "promotion",
      resource: "condition",
    },
  },
  {
    name: "WPS Sport Attendees",
    list: "/attendees/sports",
    show: "/attendees/sports/:id/detail",
    edit: "/attendees/sports/:id/edit",
    meta: {
      hide: true,
      app: "attendees",
      resource: "sports",
    },
  },
  {
    name: "WPS Attraction Attendees",
    list: "/attendees/attractions",
    show: "/attendees/attractions/:id/detail",
    meta: {
      hide: true,
      app: "attendees",
      resource: "attractions",
    },
  },
  {
    name: "WPS Organization Information",
    list: "/organization/info",
    edit: "/organization/info/edit",
    meta: {
      hide: true,
      app: "organization",
      resource: "info",
    },
  },
  {
    name: "WPS Role Management",
    list: "/organization/role",
    show: "/organization/role/:id",
    create: "/organization/role/create",
    edit: "/organization/role/:id/edit",
    meta: {
      hide: true,
      app: "organization",
      resource: "role",
    },
  },
  {
    name: "WPS Account Management",
    list: "/organization/account",
    show: "/organization/account/:id",
    create: "/organization/account/create",
    edit: "/organization/account/:id/edit",
    meta: {
      hide: true,
      app: "organization",
      resource: "account",
    },
  },
  {
    name: "WPS eInvoice",
    list: "/organization/einvoice",
    edit: "/organization/einvoice/edit",
    meta: {
      hide: true,
      app: "organization",
      resource: "einvoice",
    },
  },
  {
    name: "WPS Check-in Events",
    list: "/check-in/events",
    show: "/check-in/events/:id/detail",
    edit: "/check-in/events/:id/edit",
    meta: {
      hide: true,
      app: "check-in",
      resource: "events",
    },
  },
  {
    name: "WPS Check-in Sport Participants",
    list: "/check-in/sport-participants",
    show: "/check-in/sport-participants/:id/detail",
    edit: "/check-in/sport-participants/:id/edit",
    meta: {
      hide: true,
      app: "check-in",
      resource: "sport-participants",
    },
  },
  {
    name: "WPS Check-in Attraction Participants",
    list: "/check-in/attraction-participants",
    show: "/check-in/attraction-participants/:id/detail",
    edit: "/check-in/attraction-participants/:id/edit",
    meta: {
      hide: true,
      app: "check-in",
      resource: "attraction-participants",
    },
  },
  {
    name: "Fipix",
    meta: {
      icon: <PhotographIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Accounts",
    list: "/fipix/accounts",
    show: "/fipix/accounts/:id",
    create: "/fipix/accounts/create",
    edit: "/fipix/accounts/:id/edit",
    meta: {
      icon: <UserIcon className="nav-icon" aria-hidden="true" />,
      parent: "Fipix",
      label: "Fipix Accounts",
      app: "fipix",
      resource: "accounts",
    },
  },
  {
    name: "Events",
    list: "/fipix/events",
    show: "/fipix/events/:id",
    create: "/fipix/events/create",
    edit: "/fipix/events/:id/edit",
    meta: {
      icon: <TicketIcon className="nav-icon" aria-hidden="true" />,
      parent: "Fipix",
      label: "Fipix events management",
      app: "fipix",
      resource: "events",
    },
  },
  {
    name: "Check-in",
    meta: {
      icon: <ScanIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Event",
    identifier: "pickup-event",
    list: "/pickup/events",
    show: "/pickup/events/:id",
    create: "/pickup/events/create",
    edit: "/pickup/events/:id/edit",
    meta: {
      icon: <Ticket2Icon className="nav-icon" aria-hidden="true" />,
      parent: "Check-in",
      label: "Check-in Event Management",
      app: "pickup",
      resource: "events",
    },
  },
  {
    name: "Sport Participants",
    list: "/pickup/sport-participants",
    show: "/pickup/sport-participants/:id",
    edit: "/pickup/sport-participants/:id/edit",
    meta: {
      icon: <ShoeIcon className="nav-icon" aria-hidden="true" />,
      parent: "Check-in",
      label: "Check-in Sports Participant Management",
      app: "pickup",
      resource: "sport_participants",
    },
  },
  {
    name: "Attraction Participants",
    list: "/pickup/attraction-participants",
    show: "/pickup/attraction-participants/:id",
    edit: "/pickup/attraction-participants/:id/edit",
    meta: {
      icon: <MusicNoteIcon className="nav-icon" aria-hidden="true" />,
      parent: "Check-in",
      label: "Check-in Attraction Attendees Management",
      app: "pickup",
      resource: "attraction_participants",
    },
  },
  {
    name: "Account",
    list: "/pickup/accounts",
    show: "/pickup/accounts/:id",
    create: "/pickup/accounts/create",
    edit: "/pickup/accounts/:id/edit",
    meta: {
      icon: <UserIcon className="nav-icon" aria-hidden="true" />,
      parent: "Check-in",
      label: "Check-in Account Management",
      app: "pickup",
      resource: "accounts",
    },
  },
  {
    name: "eWaiver",
    list: "/ewaiver",
    show: "/ewaiver/detail/:id",
    edit: "/ewaiver/edit/:id",
    create: "/ewaiver/create",
    meta: {
      icon: <PencilIcon className="nav-icon" aria-hidden="true" />,
      label: "eWaiver Management",
      app: "e_waiver",
      resource: "",
      parent: "Check-in",
    },
  },
  {
    name: "MKT",
    meta: {
      icon: <CubeIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Homepage",
    list: "/mkt/homepage",
    show: "/mkt/homepage/detail",
    edit: "/mkt/homepage/edit",
    meta: {
      icon: (
        <DesktopComputerIconOutline className="nav-icon" aria-hidden="true" />
      ),
      parent: "MKT",
      app: "mkt",
      resource: "homepage",
    },
  },
  {
    name: "Banner",
    list: "/mkt/banner",
    show: "/mkt/banner/detail",
    edit: "/mkt/banner/edit",
    meta: {
      icon: <ImageIcon className="nav-icon" aria-hidden="true" />,
      parent: "MKT",
      app: "mkt",
      resource: "banner",
    },
  },
  {
    name: "Blogs",
    list: "/mkt/blog",
    show: "/mkt/blog/:id",
    create: "/mkt/blog/create",
    edit: "/mkt/blog/:id/edit",
    meta: {
      icon: <NewspaperIcon className="nav-icon" aria-hidden="true" />,
      parent: "MKT",
      app: "mkt",
      resource: "blog",
    },
  },
  {
    name: "Blogs - sub categories",
    list: "/mkt/sub-category",
    show: "/mkt/sub-category/:id",
    create: "/mkt/sub-category/create",
    edit: "/mkt/sub-category/:id/edit",
    meta: {
      icon: <ViewGridIconOutline className="nav-icon" aria-hidden="true" />,
      parent: "MKT",
      app: "mkt",
      resource: "blog_sub_categories",
    },
  },
  {
    name: "Homepage coupon",
    list: "/mkt/homepage-coupon",
    show: "/mkt/homepage-coupon/:id",
    create: "/mkt/homepage-coupon/create",
    edit: "/mkt/homepage-coupon/:id/edit",
    meta: {
      icon: <ReceiptTaxIcon className="nav-icon" aria-hidden="true" />,
      parent: "MKT",
      app: "mkt",
      resource: "homepage_coupon",
    },
  },
  {
    name: "Organization",
    meta: {
      icon: <SolidUserGroupIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Org Management",
    list: "/organization/org",
    show: "/organization/org/:id",
    create: "/organization/org/create",
    edit: "/organization/org/:id/edit",
    meta: {
      icon: <UserGroupIcon className="nav-icon" aria-hidden="true" />,
      parent: "Organization",
      app: "organization",
      resource: "org_management",
    },
  },
  {
    name: "Role Management",
    list: "/organization/roles",
    show: "/organization/roles/:id",
    create: "/organization/roles/create",
    edit: "/organization/roles/:id/edit",
    meta: {
      icon: <ClipboardCopyIcon className="nav-icon" aria-hidden="true" />,
      parent: "Organization",
      label: "Org's Role Management",
      app: "organization",
      resource: "role_management",
    },
  },
  {
    name: "Account Management",
    list: "/organization/accounts",
    show: "/organization/accounts/:id",
    create: "/organization/accounts/create",
    edit: "/organization/accounts/:id/edit",
    meta: {
      icon: <UsersIcon className="nav-icon" aria-hidden="true" />,
      parent: "Organization",
      label: "Org's Accounts Management",
      app: "organization",
      resource: "account_management",
    },
  },
  {
    name: "Other",
    meta: {
      icon: <FlagIcon className="nav-icon" aria-hidden="true" />,
    },
  },
  {
    name: "Customer accounts",
    list: "/customer-accounts",
    show: "/customer-accounts/detail/:id",
    meta: {
      icon: <UsersIconSolid className="nav-icon" aria-hidden="true" />,
      label: "Customer Account Management",
      app: "customer_accounts",
      resource: "",
      parent: "Other",
    },
  },
  {
    name: "Club",
    list: "/other/club",
    meta: {
      icon: <SolidUserGroupIcon className="nav-icon" aria-hidden="true" />,
      parent: "Other",
      app: "other",
      resource: "club",
    },
  },
  {
    name: "CS",
    meta: {
      icon: <CSIcon className="nav-icon" aria-hidden="true" />,
      label: "CS",
      parent: "Other",
    },
  },
  {
    name: "Support Categories",
    list: "/cs/support-category",
    show: "/cs/support-category/:id",
    create: "/cs/support-category/create",
    edit: "/cs/support-category/:id/edit",
    meta: {
      icon: <ViewGridIcon className="nav-icon" aria-hidden="true" />,
      parent: "CS",
      label: "Support Center Categories",
      app: "cs",
      resource: "support_categories",
    },
  },
  {
    name: "Support Q&A",
    list: "/cs/support-qa",
    show: "/cs/support-qa/:id",
    create: "/cs/support-qa/create",
    edit: "/cs/support-qa/:id/edit",
    meta: {
      icon: <QuestionMarkCircleIcon className="nav-icon" aria-hidden="true" />,
      parent: "CS",
      label: "Support Center Q&A",
      app: "cs",
      resource: "support_qa",
    },
  },
  {
    name: "Contacts",
    list: "/cs/contacts",
    meta: {
      icon: <MailIcon className="nav-icon" aria-hidden="true" />,
      parent: "CS",
      app: "cs",
      resource: "contact",
    },
  },
  {
    name: "Admin accounts",
    meta: {
      icon: <UserIconOutline className="nav-icon" aria-hidden="true" />,
      parent: "Other",
    },
  },
  {
    name: "Roles",
    list: "/admin-accounts/roles",
    show: "/admin-accounts/roles/:id",
    create: "/admin-accounts/roles/create",
    edit: "/admin-accounts/roles/:id/edit",
    meta: {
      icon: <ClipboardCopyIcon className="nav-icon" aria-hidden="true" />,
      parent: "Admin accounts",
      label: "Role Management",
      app: "admin",
      resource: "role",
    },
  },
  {
    name: "Accounts",
    list: "/admin-accounts/accounts",
    show: "/admin-accounts/accounts/:id",
    create: "/admin-accounts/accounts/create",
    edit: "/admin-accounts/accounts/:id/edit",
    meta: {
      icon: <UsersIconSolid className="nav-icon" aria-hidden="true" />,
      parent: "Admin accounts",
      label: "Admin Accounts Management",
      app: "admin",
      resource: "account",
    },
  },
  {
    name: "About Actiup",
    list: "/other/about-actiup",
    show: "/other/about-actiup/:id",
    edit: "/other/about-actiup/:id/edit",
    meta: {
      icon: <DesktopComputerIcon className="nav-icon" aria-hidden="true" />,
      parent: "Other",
      app: "other",
      resource: "about_actiup",
    },
  },
  {
    name: "Exchange rate",
    list: "/other/exchange-rate",
    meta: {
      icon: <SwitchHorizontalIcon className="nav-icon" aria-hidden="true" />,
      parent: "Other",
      app: "other",
      resource: "exchange_rate",
    },
  },
];
